import { CheckOutlined, CloseOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons'
import { Button, Input, Tooltip, message } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import {
    testRunDetail,
    testRunState,
} from '../../../../redux/testRun/testRunList.slice'
import SkeletonBasicDetails from './SkeletonBasicDetails'
import { apiUpdateTestReportName } from '../../../../redux/testRun/testRun.service'
import styles from '../../../../testrun.module.css'

const MAX_NAME_LENGTH = 120

const BasicDetails = ({ setOpen = () => { } }) => {
    const dispatch = useDispatch()
    const { id: routeId } = useParams()
    const { testRunDetail: testRunDetailData, testRunDetailLoading } =
        useSelector(testRunState)

    const reportName = testRunDetailData?.name || ''
    const reportId =
        testRunDetailData?._id || testRunDetailData?.id || routeId || ''

    const displayName = reportName || 'Report Details'

    const [nameState, setNameState] = useState({
        editing: false,
        draft: reportName,
        saving: false,
        truncated: false,
    })
    const { editing, draft, saving, truncated } = nameState
    const nameInputRef = useRef(null)
    const nameTextRef = useRef(null)

    const patchNameState = (patch) =>
        setNameState((prev) => ({ ...prev, ...patch }))

    useEffect(() => {
        if (!editing) patchNameState({ draft: reportName })
    }, [reportName, editing])

    useEffect(() => {
        if (!editing) return
        nameInputRef.current?.focus?.()
        nameInputRef.current?.select?.()
    }, [editing])

    useEffect(() => {
        const node = nameTextRef.current
        if (!node) return undefined

        const measure = () => {
            const isTruncated = node.scrollWidth > node.clientWidth + 1
            setNameState((prev) =>
                prev.truncated === isTruncated
                    ? prev
                    : { ...prev, truncated: isTruncated }
            )
        }

        measure()

        if (typeof ResizeObserver === 'undefined') return undefined

        const observer = new ResizeObserver(measure)
        observer.observe(node)
        return () => observer.disconnect()
    }, [displayName, editing])

    if (testRunDetailLoading) {
        return <SkeletonBasicDetails />
    }

    const cancelEditName = () =>
        patchNameState({ editing: false, draft: reportName })

    const validateName = (value) => {
        if (!value.trim()) {
            return 'Report name cannot be only spaces. Please enter valid text.'
        }
        if (value !== value.trim()) {
            return 'Please remove extra spaces at the start or end of report name'
        }
        if (value.length > MAX_NAME_LENGTH) {
            return `Report name must be ${MAX_NAME_LENGTH} characters or less`
        }
        return ''
    }

    const saveEditName = async () => {
        if (saving) return

        const error = validateName(draft)
        if (error) {
            message.error(error)
            return
        }

        if (draft === reportName) {
            patchNameState({ editing: false })
            return
        }

        patchNameState({ saving: true })
        try {
            const resp = await apiUpdateTestReportName({
                id: reportId,
                name: draft,
            })

            if (resp?.data?.success) {
                message.success('Report name updated successfully')
                dispatch(testRunDetail(reportId))
                patchNameState({ editing: false, saving: false })
                return
            }

            message.error(
                resp?.data?.message || 'Failed to update report name'
            )
        } catch {
            message.error('Failed to update report name')
        }

        patchNameState({ saving: false })
    }

    return (
        <div className={styles.detailHeader}>
            <div className={styles.sectionHeader}>
                <div className={styles.pageHeaderText}>
                    {editing ? (
                        <div className={styles.pageTitleEditRow}>
                            <Input
                                ref={nameInputRef}
                                value={draft}
                                maxLength={MAX_NAME_LENGTH}
                                placeholder="Enter report name"
                                className={styles.pageTitleInput}
                                disabled={saving}
                                onChange={(e) =>
                                    patchNameState({ draft: e.target.value })
                                }
                                onPressEnter={saveEditName}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') cancelEditName()
                                }}
                            />
                            <Button
                                type="text"
                                className={`${styles.pageTitleAction} ${styles.pageTitleActionSave}`}
                                aria-label="Save report name"
                                icon={<CheckOutlined />}
                                loading={saving}
                                onClick={saveEditName}
                            />
                            <Button
                                type="text"
                                className={`${styles.pageTitleAction} ${styles.pageTitleActionCancel}`}
                                aria-label="Cancel editing report name"
                                icon={<CloseOutlined />}
                                disabled={saving}
                                onClick={cancelEditName}
                            />
                        </div>
                    ) : (
                        <div className={styles.pageTitleRow}>
                            <Tooltip
                                title={truncated ? displayName : ''}
                                placement="topLeft"
                            >
                                <h3
                                    ref={nameTextRef}
                                    className={styles.pageTitle}
                                >
                                    {displayName}
                                </h3>
                            </Tooltip>
                            {reportId && (
                                <Tooltip title="Edit report name">
                                    <Button
                                        type="text"
                                        className={styles.pageTitleAction}
                                        aria-label="Edit report name"
                                        icon={<EditOutlined />}
                                        onClick={() =>
                                            patchNameState({ editing: true })
                                        }
                                    />
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>

                <div className={styles.toolbarActions}>
                    <Button
                        className={styles.secondaryButton}
                        onClick={() => setOpen(true)}
                        icon={<UploadOutlined />}
                    >
                        Export Report
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default BasicDetails
